import { useMemo } from "react";
import { AskMonitoringChat } from "./AskMonitoringChat";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { useMakeUrl, routes } from "../../lib/urls";
import { useMonitoringInteraction } from "./analytics";
import { DonutChart } from "../DonutChart";
import {
  buildSpaceSegments,
  OTHER_SPACES_KEY,
  resolveFoldedSpaceIds,
  type SpaceSegmentRow,
} from "./spaceSegments";
import { Text } from "../Text";
import {
  formatExecutionCount,
  workflowRowsForSpaceSegment,
} from "./executionCounts";
import { InfoCorner, PanelBadge } from "./PanelInfo";
import { spaceColorMap } from "./monitoringPanelLayout";
import {
  foldsPersonal,
  PERSONAL_BUCKET,
  PERSONAL_BUCKET_COLOR,
} from "./monitoringScope";
import { useMonitoringScope } from "./useMonitoringScope";
import {
  useMonitoringSpaces,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";
import { useSpaceDrilldown } from "./useSpaceDrilldown";
import { WorkflowListModal } from "./WorkflowListModal";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

export function ActiveWorkflowsBySpace() {
  const makeUrl = useMakeUrl();
  const recordInteraction = useMonitoringInteraction();
  const { report, loading } = useMonitoringWorkflows();
  const { spaces, loading: spacesLoading } = useMonitoringSpaces();
  const { scope, personalIds, matchesSpace } = useMonitoringScope(
    routes.monitoring.workflows
  );
  const displayName = useSpaceDisplayName();
  const {
    open,
    selectedSpaceId: selectedSegmentId,
    search,
    setSearch,
    close,
  } = useSpaceDrilldown("active");

  const workflows = useMemo(
    () => report.workflows.filter((workflow) => workflow.active),
    [report]
  );

  const { chartRows, total } = useMemo(() => {
    const countBySpace = new Map<string, number>();
    for (const workflow of workflows) {
      countBySpace.set(
        workflow.spaceId,
        (countBySpace.get(workflow.spaceId) ?? 0) + 1
      );
    }

    const chartRows: SpaceSegmentRow[] = [];
    let personalCount = 0;
    for (const space of spaces) {
      if (!matchesSpace(space.id)) continue;
      const count = countBySpace.get(space.id) ?? 0;
      if (count === 0) continue;
      if (foldsPersonal(scope) && personalIds.has(space.id)) {
        personalCount += count;
        continue;
      }
      chartRows.push({ id: space.id, label: displayName(space), value: count });
    }
    if (personalCount > 0) {
      chartRows.push({
        id: PERSONAL_BUCKET,
        label: "Personal",
        value: personalCount,
      });
    }
    chartRows.sort(
      (a, b) => b.value - a.value || a.label.localeCompare(b.label)
    );
    const total = chartRows.reduce((sum, row) => sum + row.value, 0);
    return { chartRows, total };
  }, [workflows, spaces, matchesSpace, displayName, scope, personalIds]);

  const colorMap = useMemo(() => {
    const map = new Map(spaceColorMap(spaces));
    map.set(PERSONAL_BUCKET, PERSONAL_BUCKET_COLOR);
    return map;
  }, [spaces]);

  const segments = useMemo(
    () => buildSpaceSegments(chartRows, total, colorMap),
    [chartRows, total, colorMap]
  );

  const foldedSpaceIds = resolveFoldedSpaceIds(
    chartRows,
    segments,
    personalIds
  );

  const selectedSegment = segments.find(
    (segment) => segment.key === selectedSegmentId
  );
  const selectedSpace =
    selectedSegment &&
    selectedSegment.key !== PERSONAL_BUCKET &&
    selectedSegment.key !== OTHER_SPACES_KEY
      ? spaces.find((entry) => entry.id === selectedSegment.key)
      : undefined;
  const selectedWorkflowRows = selectedSegment
    ? workflowRowsForSpaceSegment(
        workflows,
        selectedSegment.key,
        personalIds,
        foldedSpaceIds
      )
    : [];
  if (loading || spacesLoading) {
    return <PanelSkeleton />;
  }

  return (
    <Panel>
      <InfoCorner>
        <PanelBadge>Last 30 days</PanelBadge>
      </InfoCorner>
      <PanelHeader>
        <Text variant="text-small-md">Active workflows by space</Text>
        <TotalRow>
          <Text variant="display-md">{total}</Text>
        </TotalRow>
      </PanelHeader>
      <DonutChart
        segments={segments}
        total={total}
        placeholder={total === 0}
        strokeWidth={6}
        centerCaption="workflows"
        scrollableLegend
        segmentHref={(s) =>
          makeUrl(routes.monitoring.workflows, {
            scope,
            panel: "active",
            space: s.key,
            search: "",
          })
        }
        onSegmentNavigate={(s) =>
          recordInteraction("workflows.panel_drilldown_open", {
            targetType: "space",
            targetId: s.key,
            value: "active",
          })
        }
      />

      <AskMonitoringChat
        surface="active-workflows"
        snapshot={`${total} active workflows across ${chartRows.length} displayed space groups`}
        context={{ filters: { spaceId: null, view: "by-space" } }}
      />

      <WorkflowListModal
        open={open}
        onClose={close}
        icon="product-workflows"
        title="Active workflows"
        space={selectedSpace}
        sectionTitle="Active workflows"
        description="Workflows with activity on the live branch over the last 30 days."
        rows={selectedWorkflowRows}
        search={search}
        onSearchChange={setSearch}
        emptyMessage="No active workflows."
        renderFooter={(visibleCount) =>
          selectedSegment
            ? `${formatExecutionCount(visibleCount)} active ${visibleCount === 1 ? "workflow" : "workflows"}`
            : null
        }
      />
    </Panel>
  );
}
